import { useMutation } from "@apollo/client/react";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";

import type { JobRun } from "~/components/job-log";
import {
  SubmitBatchEvidenceCommitsJobDocument,
  type SubmitBatchEvidenceCommitsJobMutation,
  type SubmitBatchEvidenceCommitsJobMutationVariables,
} from "~/features/dataImportProjects/gqls";
import { useTenant } from "~/features/tenant/context";
import { useActionWithConfirm } from "~/lib/handleActionWithConfirm";

type UseSubmitBatchEvidenceCommitsJobProps = {
  /** The BEJob whose drafts will be committed. */
  batchEvidencesJobId: string;
  refetch: () => void;
  setLogDialogJobRun?: (jobRun: JobRun | null) => void;
  canExecute?: () => boolean;
};

/**
 * Launch a BatchEvidenceCommitsJob (BECJob) that commits the BEJob's evidence drafts into evidences.
 * The dry run is count-only (no dryRunOptions); the live run commits the drafts.
 */
export const useSubmitBatchEvidenceCommitsJob = ({
  batchEvidencesJobId,
  refetch,
  setLogDialogJobRun,
  canExecute,
}: UseSubmitBatchEvidenceCommitsJobProps) => {
  const { t } = useTranslation();
  const actionWithConfirm = useActionWithConfirm();
  const [processing, setProcessing] = React.useState<{ value: boolean; dryRun: boolean }>({
    value: false,
    dryRun: false,
  });
  const [submitJob] = useMutation<
    SubmitBatchEvidenceCommitsJobMutation,
    SubmitBatchEvidenceCommitsJobMutationVariables
  >(SubmitBatchEvidenceCommitsJobDocument, {
    // Callers trigger a targeted `refetch()`; keep this empty to avoid bare refetches that fail variable validation.
    refetchQueries: [],
  });
  const { tenantId } = useTenant();

  const submit = useCallback(
    async (dryRun: boolean) => {
      setProcessing({ value: true, dryRun });
      const res = await actionWithConfirm({
        // The caller owns the confirmation dialog, so asking again here would double it.
        skipConfirm: true,
        confirmMessage: t("feature.dataImportProjects.detail.jobs.confirmEvidenceCommitsJobRun"),
        successMessage: t("feature.dataImportProjects.detail.notify.submitJobSuccessfully"),
        canExecute,
        action: async () => {
          if (!tenantId) {
            throw new Error("Tenant is not resolved");
          }

          const { data } = await submitJob({
            variables: { input: { tenantId, batchEvidencesJobId, dryRun } },
          });
          const ret = data?.cnutSubmitBatchEvidenceCommitsJob;
          if (!ret) {
            throw new Error("failed to submit batch evidence commits job");
          }
          if (!("jobRunId" in ret)) {
            throw new Error(`error message: ${ret.message}`);
          }
          return ret;
        },
        successAction: (result) => {
          if (result?.jobRunId && setLogDialogJobRun) {
            setLogDialogJobRun({
              jobRunId: result.jobRunId,
              jobName: "BatchEvidenceCommitsJob",
              dryRun,
            });
          }
        },
        finallyAction: () => {
          setProcessing({ value: false, dryRun: false });
        },
      });
      refetch();

      return res;
    },
    [actionWithConfirm, batchEvidencesJobId, canExecute, submitJob, t, refetch, setLogDialogJobRun, tenantId],
  );

  return {
    submit,
    processing,
  };
};
